import type { MeResponse, PermissionEntry } from "../api/schemas";

/* ──────────────────────────────────────────────────────────────────────
   The backend emits BOTH page-level rows (feature=null) and feature-level
   rows. For nav visibility we only consult the page-level rows.
   ────────────────────────────────────────────────────────────────────── */

export interface NavGroup {
  /** Section label shown in the sidebar (small-caps). */
  section: string;
  items: ReadonlyArray<NavItem>;
}

export interface NavItem {
  label: string;
  to: string;
  /**
   * One or more page slugs from WebUser_PageMaster. The item shows if at least
   * one of them has a page-level (feature=null) entry with show=true.
   */
  anyOf: ReadonlyArray<string>;
  /** Folio marker shown right of the link (Roman numeral / section letter). */
  marker?: string;
  /**
   * Hard group gate. When set, the item shows ONLY to these groups (and `anyOf` is not
   * consulted). Use for SuperAdmin-only features that have no WebUser_PageMaster slug; the
   * route must also be wrapped in <RequireGroup> and the API must enforce the same.
   */
  groups?: ReadonlyArray<string>;
}

export const NAV: ReadonlyArray<NavGroup> = [
  {
    section: "Home",
    items: [
      { label: "Today",         to: "/",        anyOf: ["__home__"],          marker: "·" },
      { label: "Publishers",    to: "/browse",  anyOf: ["Newspaper_Browse"],  marker: "I" },
    ],
  },
  {
    section: "Operations",
    items: [
      {
        label: "Crawler · Publishers",
        to: "/crawler-ops/publishers",
        anyOf: [
          "Newspaper_CrawlerOps_TodayRuns",
          "Newspaper_CrawlerOps_Queue",
          "Newspaper_CrawlerOps_Attempts",
          "Newspaper_CrawlerOps_Alerts",
          "Newspaper_CrawlerOps_Heartbeat",
          "Newspaper_CrawlerOps_Audit",
        ],
        marker: "III",
      },
      {
        label: "Crawler · Channels",
        to: "/crawler-ops/channels",
        anyOf: [
          "Newspaper_CrawlerOps_TodayRuns",
          "Newspaper_CrawlerOps_Queue",
          "Newspaper_CrawlerOps_Attempts",
          "Newspaper_CrawlerOps_Alerts",
          "Newspaper_CrawlerOps_Heartbeat",
          "Newspaper_CrawlerOps_Audit",
        ],
        marker: "III·b",
      },
      {
        label: "Crawler · Sessions",
        to: "/crawler-ops/sessions",
        anyOf: [
          "Newspaper_CrawlerOps_TodayRuns",
          "Newspaper_CrawlerOps_Queue",
          "Newspaper_CrawlerOps_Attempts",
          "Newspaper_CrawlerOps_Alerts",
          "Newspaper_CrawlerOps_Heartbeat",
          "Newspaper_CrawlerOps_Audit",
        ],
        marker: "III·c",
      },
      {
        label: "Crawler · Coverage",
        to: "/crawler-ops/coverage",
        anyOf: [
          "Newspaper_CrawlerOps_TodayRuns",
          "Newspaper_CrawlerOps_Queue",
          "Newspaper_CrawlerOps_Attempts",
          "Newspaper_CrawlerOps_Alerts",
          "Newspaper_CrawlerOps_Heartbeat",
          "Newspaper_CrawlerOps_Audit",
        ],
        marker: "III·d",
      },
      {
        label: "Masters",
        to: "/masters",
        anyOf: [
          "Newspaper_Masters_PublisherGroup",
          "Newspaper_Masters_Editions",
          "Newspaper_Masters_CrawlerConfig",
          "Newspaper_Masters_AuthProfile",
          "Newspaper_Masters_Storage",
        ],
        marker: "IV",
      },
    ],
  },
  {
    section: "Search",
    items: [
      // SuperAdmin-only full-text search over OCR'd articles (ES print-articles). Gated at
      // three layers: this nav item, <RequireGroup> on the route, require_super_admin on the API.
      { label: "Print search", to: "/print-search", anyOf: [], groups: ["SuperAdmin", "Admin"], marker: "S" },
    ],
  },
  {
    section: "Clips",
    items: [
      {
        label: "Clips",
        to: "/clips",
        anyOf: ["Newspaper_ClipWorkspace", "Newspaper_ClipReview"],
        marker: "V",
      },
    ],
  },
  {
    section: "Administration",
    items: [
      {
        label: "Users",
        to: "/users",
        anyOf: [
          "Newspaper_Users_List",
          "Newspaper_Users_Groups",
          "Newspaper_Users_Rights",
        ],
        marker: "VI",
      },
      {
        label: "Sessions",
        to: "/users/sessions",
        anyOf: ["Newspaper_Users_Sessions"],
        marker: "VI·b",
      },
      {
        // SuperAdmin-only: the route itself is guarded by RequireGroup; the
        // nav item shows for anyone holding the Users_Rights page, which in
        // the seeded rights matrix is SuperAdmin alone.
        label: "Agent 1",
        to: "/agent",
        anyOf: ["Newspaper_Users_Rights"],
        marker: "VII",
      },
      {
        // Agent 2 — same model and tools, the v2 loop (stall recovery, mid-task
        // steering, ask-the-operator). Runs alongside Agent 1 so the two can be
        // compared on the same task. Same SuperAdmin gate.
        label: "Agent 2",
        to: "/agent2",
        anyOf: ["Newspaper_Users_Rights"],
        marker: "VII·b",
      },
      {
        // Agent 3 — Agent 2's loop plus the live capture browser, screenshots with
        // mark-up and session capture from the page. Same SuperAdmin gate.
        label: "Agent 3",
        to: "/agent3",
        anyOf: ["Newspaper_Users_Rights"],
        marker: "VII·c",
      },
    ],
  },
];

/** True if the user has any page-level Show right for one of `slugs`. */
export function canSeeAny(me: MeResponse, slugs: ReadonlyArray<string>): boolean {
  for (const p of me.permissions) {
    if (p.feature !== null) continue;        // page-level rows only
    if (slugs.includes(p.page) && p.show) return true;
  }
  return false;
}

/** Page-level rights for a single slug, or null if user has none. */
export function pageRights(
  me: MeResponse,
  slug: string
): PermissionEntry | null {
  return (
    me.permissions.find((p) => p.feature === null && p.page === slug) ?? null
  );
}

/** Visibility filter applied to the NAV array. */
export function visibleNav(me: MeResponse): NavGroup[] {
  return NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) =>
      item.groups
        ? item.groups.includes(me.group_name)
        : item.anyOf.includes("__home__") ? true : canSeeAny(me, item.anyOf)
    ),
  })).filter((g) => g.items.length > 0);
}

/** Compact summary of unique pages the user can see — used by Home. */
export function visiblePages(me: MeResponse): Array<{ page: string; label: string }> {
  const labels: Record<string, string> = {
    Newspaper_Browse: "Browse — issues by publisher and date",
    Newspaper_Reader: "Reader — full-page newspaper reader",
    Newspaper_CrawlerOps_TodayRuns: "Crawler Ops — today's runs",
    Newspaper_CrawlerOps_Queue: "Crawler Ops — job queue",
    Newspaper_CrawlerOps_Attempts: "Crawler Ops — attempt history",
    Newspaper_CrawlerOps_Alerts: "Crawler Ops — alerts",
    Newspaper_CrawlerOps_Heartbeat: "Crawler Ops — worker heartbeat",
    Newspaper_CrawlerOps_Audit: "Crawler Ops — audit log",
    Newspaper_Masters_PublisherGroup: "Masters — publisher groups",
    Newspaper_Masters_Editions: "Masters — editions catalog",
    Newspaper_Masters_CrawlerConfig: "Masters — crawler config templates",
    Newspaper_Masters_AuthProfile: "Masters — auth profiles (cookies, creds)",
    Newspaper_Masters_Storage: "Masters — storage browser",
    Newspaper_Users_List: "Users — staff accounts",
    Newspaper_Users_Groups: "Users — groups + rights matrix",
    Newspaper_Users_Rights: "Users — per-user rights overrides",
    Newspaper_Users_Sessions: "Users — active sessions",
    Newspaper_Agent_Console: "Agent — operator agent console",
    Newspaper_CrawlerOps_Coverage: "Crawler Ops — coverage (expected vs produced)",
    Newspaper_ClipWorkspace: "Clips — crop and tag from a page",
    Newspaper_ClipReview: "Clips — review queue (approve / reject)",
    Newspaper_EntityMasters_Client: "Entities — client master",
    Newspaper_EntityMasters_Company: "Entities — company master",
    Newspaper_EntityMasters_Brand: "Entities — brand master",
    Newspaper_EntityMasters_People: "Entities — people master",
  };
  const seen = new Set<string>();
  const out: Array<{ page: string; label: string }> = [];
  for (const p of me.permissions) {
    if (p.feature !== null) continue;
    if (!p.show) continue;
    if (seen.has(p.page)) continue;
    seen.add(p.page);
    out.push({ page: p.page, label: labels[p.page] ?? p.page });
  }
  out.sort((a, b) => a.page.localeCompare(b.page));
  return out;
}
